import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import './clientDashboardMap.css';
import PortalHeader from '../client-dashboard/clientNavbar/PortalHeader';
import LoadingSpinner from '../backend/components/LoadingSpinner';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Assets
import googleMapsLogo from '../backend/assets/google-map-logo.png'
import fnnPopupLogo from '../img/detector/2025Detector4.jpg'
import greenMarker from './client-dashboard-assets/mapdetectoricon-blue.png'
import offlineMarker from './client-dashboard-assets/mapdetectoricon-grey.png'

// Map functionality and Design imports
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    useMap,
    GeoJSON,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/dist/styles.min.css";
import { Icon } from "leaflet";
import L, { LeafletEvent, GeoJSON as LeafletGeoJSON } from "leaflet";
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import "leaflet-geosearch/dist/geosearch.css";
import type { Layer, LeafletMouseEvent, StyleFunction } from "leaflet";
import type { Feature, Geometry, FeatureCollection } from "geojson";
import HeatmapOverlay from "leaflet-heatmap";

// County GeoJSON (same file used in GisLightningAnalysis)
import countiesGeoJson from '../team-portal/GisTab/counties_southeast.json';

// ------------- Popup imports -------------
import Modal from "../components/UtilComponents/Modal";
import { useAuth } from '../Auth';
// ------------- Popup imports -------------

// CSRF imports
import Cookies from 'universal-cookie';
const cookies = new Cookies();

// Icons
import { TbCalendarPlus } from "react-icons/tb";
import { MdOutlineEditCalendar } from "react-icons/md";
import { FaPlus, FaLock } from "react-icons/fa6";
import { BsLightningChargeFill } from "react-icons/bs";
import { HiOutlineFilter } from "react-icons/hi";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Detector {
    ip: string
    last_active: string
    mac: string
    online: boolean
    status: {
        fw_version: any
        gps_fix: boolean
        gps_lat: any
        gps_long: any
        linux: boolean
        memory: number
        site_name: string
        test_mode: boolean
        thr: any
        up_since: string
    }
}

interface DetectorObject {
    name: string
    receiver: string
}

interface DetectorPhoto {
    IMAGE_ID: number;
    DETECTOR_ID: string;
    DETECTOR_SITE_NAME: string;
    DETECTOR_IMG: any;
    organization?: number;
}

interface DetectorNotes {
    NOTE_ID?: number;
    DETECTOR_ID: string;
    NOTE_TITLE: string;
    NOTE_SUBMIT_DATE: string;
    NOTE_EDIT_DATE: any;
    NOTE_BODY: string;
    organization?: number;
}

interface GisArchiveLightning {
    lightning_group_id: number;
    strikes: Record<string, number>;
    counties: Record<string, number>;
    date: string;
    region: string;
}

interface CountyFeatureProps {
    NAME: string;
    STATEFP: string;
    STATE?: string;
    [key: string]: any;
}

interface HeatPoint {
    lat: number;
    lng: number;
    count: number;
}

// Lightning overlay mode
type LightningMode = 'none' | 'yesterday' | 'past3days' | 'past7days';
type LightningLayer = 'heatmap' | 'county';

// ─── Constants ───────────────────────────────────────────────────────────────

const stateToAbbrev: Record<string, string> = {
    Florida: "FL",
    Alabama: "AL",
    Georgia: "GA",
    Texas: "TX",
    "South Carolina": "SC",
};

function normalizeMac(mac: string): string {
    return mac?.toLowerCase().replace(/^(0x|000)/, "") || "";
}

export const BwIcon = new Icon({
    iconUrl: offlineMarker,
    iconSize: [50, 50],

    // Bottom center of icon
    iconAnchor: [25, 50],

    // Popup opens above marker tip
    popupAnchor: [0, -45],
});

export const greenIcon = new Icon({
    iconUrl: greenMarker,
    iconSize: [50, 50],

    // Bottom center of icon
    iconAnchor: [25, 50],

    // Popup opens above marker tip
    popupAnchor: [0, -45],
});

const getTodayDateString = (): string => {
    const today = new Date();
    return today.toISOString().split('T')[0];
};

const getColorForMarker = (isOn: boolean) => isOn ? greenIcon : BwIcon;

/** Extract a capitalised state name from a savedGroup string like 'detectors_florida' */
function groupToStateName(group: string | null): string | null {
    if (!group) return null;
    const raw = group
        .replace(/^detectors_/, '')
        .replace(/_/g, ' ')
        .trim();
    // Title-case each word
    return raw.replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Lightning API helpers ────────────────────────────────────────────────────

/** Returns ISO date strings for [startDate, endDate] given a mode */
function getDateRangeForMode(mode: LightningMode): { start: string; end: string } | null {
    if (mode === 'none') return null;
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const fmt = (d: Date) => d.toISOString().split('T')[0];

    if (mode === 'yesterday') {
        return { start: fmt(yesterday), end: fmt(yesterday) };
    }
    if (mode === 'past3days') {
        const start = new Date(today);
        start.setDate(today.getDate() - 3);
        return { start: fmt(start), end: fmt(yesterday) };
    }
    if (mode === 'past7days') {
        const start = new Date(today);
        start.setDate(today.getDate() - 7);
        return { start: fmt(start), end: fmt(yesterday) };
    }
    return null;
}

/** Parse polygon key → heatmap midpoint */
function parseStrikesToHeatPoints(strikes: Record<string, number>): HeatPoint[] {
    const points: HeatPoint[] = [];
    for (const [key, count] of Object.entries(strikes)) {
        try {
            const coords: [number, number][] = JSON.parse(key);
            const midLng = (coords[0][0] + coords[2][0]) / 2;
            const midLat = (coords[0][1] + coords[2][1]) / 2;
            points.push({ lat: midLat, lng: midLng, count });
        } catch {
            // skip malformed keys
        }
    }
    return points;
}

// ─── Leaflet heatmap layer ────────────────────────────────────────────────────

interface HeatMapArchiveLayerProps {
    data: HeatPoint[];
    onExtremaChange?: (min: number, max: number) => void;
}

const HeatMapArchiveLayer: React.FC<HeatMapArchiveLayerProps> = ({ data, onExtremaChange }) => {
    const map = useMap();

    useEffect(() => {
        if (!map || data.length === 0) return;

        const cfg = {
            radius: 0.125,
            maxOpacity: 0.7,
            minOpacity: 0.1,
            scaleRadius: true,
            useLocalExtrema: true,
            latField: 'lat',
            lngField: 'lng',
            valueField: 'count',
            onExtremaChange: (d: { min: number; max: number }) => {
                onExtremaChange?.(d.min, d.max);
            },
        };

        const heat = new HeatmapOverlay(cfg);
        const maxVal = Math.max(...data.map(d => d.count));
        heat.setData({ max: maxVal, data });
        map.addLayer(heat);

        return () => { map.removeLayer(heat); };
    }, [map, data, onExtremaChange]);

    return null;
};

// ─── County choropleth helpers ────────────────────────────────────────────────

function getCountyColor(count: number, max: number): string {
    if (max <= 0) return '#10B981';
    const ratio = Math.min(Math.max(count / max, 0), 1);
    const stops: [number, [number, number, number]][] = [
        [0, [16, 185, 129]],
        [0.5, [250, 204, 21]],
        [1, [239, 68, 68]],
    ];
    let lower = stops[0];
    let upper = stops[stops.length - 1];
    for (let i = 0; i < stops.length - 1; i++) {
        if (ratio >= stops[i][0] && ratio <= stops[i + 1][0]) {
            lower = stops[i]; upper = stops[i + 1]; break;
        }
    }
    const span = upper[0] - lower[0] || 1;
    const t = (ratio - lower[0]) / span;
    const r = Math.round(lower[1][0] + (upper[1][0] - lower[1][0]) * t);
    const g = Math.round(lower[1][1] + (upper[1][1] - lower[1][1]) * t);
    const b = Math.round(lower[1][2] + (upper[1][2] - lower[1][2]) * t);
    return `rgb(${r}, ${g}, ${b})`;
}

// ─── County choropleth layer ──────────────────────────────────────────────────

interface CountyChoroplethLayerProps {
    countyTotals: Record<string, number>;
}

const CountyChoroplethLayer: React.FC<CountyChoroplethLayerProps> = ({ countyTotals }) => {
    const geoJsonRef = useRef<LeafletGeoJSON | null>(null);
    const [geoData, setGeoData] = useState<FeatureCollection | null>(null);

    useEffect(() => {
        setGeoData(countiesGeoJson as FeatureCollection);
    }, []);

    const { lookup, maxCount } = useMemo(() => {
        const lk: Record<string, number> = {};
        let max = 0;
        for (const [key, total] of Object.entries(countyTotals)) {
            lk[key] = total;
            if (total > max) max = total;
        }
        return { lookup: lk, maxCount: max };
    }, [countyTotals]);

    const getCount = useCallback((key: string): number => lookup[key] ?? 0, [lookup]);

    const filteredGeoData = useMemo<FeatureCollection | null>(() => {
        if (!geoData) return null;
        return {
            ...geoData,
            features: geoData.features.filter((feature) => {
                const name = feature.properties?.NAME ?? '';
                const state = feature.properties?.STATE ?? '';
                const abbrev = stateToAbbrev[state] ?? state;
                return getCount(`${name} (${abbrev})`) > 0;
            }),
        };
    }, [geoData, getCount]);

    const styleFn: StyleFunction<CountyFeatureProps> = useCallback((feature) => {
        const name = feature?.properties?.NAME ?? '';
        const state = feature?.properties?.STATE ?? '';
        const abbrev = stateToAbbrev[state] ?? state;
        const count = getCount(`${name} (${abbrev})`);
        return {
            fillColor: getCountyColor(count, maxCount),
            fillOpacity: 0.65,
            color: '#374151',
            weight: 1,
            interactive: count > 0,
        };
    }, [getCount, maxCount]);

    const onEachFeature = useCallback((feature: Feature<Geometry, CountyFeatureProps>, layer: Layer) => {
        const name = feature.properties?.NAME ?? '';
        const state = feature.properties?.STATE ?? '';
        const abbrev = stateToAbbrev[state] ?? state;
        const count = getCount(`${name} (${abbrev})`);

        layer.bindTooltip(
            `<strong>${name} County (${abbrev})</strong><br/>${count.toLocaleString()} strike${count === 1 ? '' : 's'}`,
            { sticky: false, direction: 'top' }
        );

        layer.on({
            mouseover: (e: LeafletMouseEvent) => {
                e.target.setStyle({ weight: 3, color: '#1F2937', fillOpacity: 0.85 });
                e.target.bringToFront();
                e.target.openTooltip();
            },
            mouseout: (e: LeafletMouseEvent) => {
                geoJsonRef.current?.resetStyle(e.target);
                e.target.closeTooltip();
            },
        });
    }, [getCount]);

    if (!filteredGeoData) return null;

    return (
        <GeoJSON
            key={`county-${Object.keys(countyTotals).length}-${maxCount}`}
            ref={geoJsonRef}
            data={filteredGeoData}
            style={styleFn as any}
            onEachFeature={onEachFeature as any}
        />
    );
};

// ─── Legends ─────────────────────────────────────────────────────────────────

interface CountyLegendProps { max: number; }
const CountyLegend: React.FC<CountyLegendProps> = ({ max }) => (
    <div style={{
        background: 'rgba(255,255,255,0.88)', borderRadius: 6,
        padding: '8px 12px', fontSize: 12, boxShadow: '0 1px 5px rgba(0,0,0,.2)', width: '100%'
    }}>
        <div style={{ marginBottom: 4, fontWeight: 600 }}>Strikes by County</div>
        <div style={{
            width: '100%', height: 12, borderRadius: 4,
            background: 'linear-gradient(to right, #10B981, #FACC15, #EF4444)',
            marginBottom: 4,
        }} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>1</span><span>{max > 0 ? max.toLocaleString() : '…'}</span>
        </div>
    </div>
);

interface HeatLegendProps { min: number; max: number | null; }
const HeatLegend: React.FC<HeatLegendProps> = ({ min, max }) => (
    <div style={{
        background: 'rgba(255,255,255,0.88)', borderRadius: 6,
        padding: '8px 12px', fontSize: 12, boxShadow: '0 1px 5px rgba(0,0,0,.2)', width: '100%'
    }}>
        <div style={{ marginBottom: 4, fontWeight: 600 }}>Strike Density</div>
        <div style={{
            width: "100%", height: 12, borderRadius: 4,
            background: 'linear-gradient(to right, #0000ff, #00ffff, #00ff00, #ffff00, #ff0000)',
            marginBottom: 4,
        }} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{min}</span><span>{max ?? '…'}</span>
        </div>
    </div>
);

// ─── Upgrade popup ────────────────────────────────────────────────────────────

const UpgradePopup: React.FC<{ onClose: () => void }> = ({ onClose }) => (
    <div className="upgrade-popup-overlay" onClick={onClose}>
        <div className="upgrade-popup-card" onClick={(e) => e.stopPropagation()}>
            <div className="upgrade-popup-icon">
                <FaLock />
            </div>
            <h2 className="upgrade-popup-title">Pro Feature</h2>
            <p className="upgrade-popup-body">
                This option is available on the <strong>Pro</strong> and <strong>Enterprise</strong> plans.
                Upgrade to unlock additional date ranges and county-level breakdown.
            </p>
            <a
                href="/fnn_portal_representative"
                className="upgrade-popup-cta"
            >
                Upgrade Plan
            </a>
            <button className="upgrade-popup-dismiss" onClick={onClose}>
                Maybe later
            </button>
        </div>
    </div>
);

// ─── Map helpers (unchanged) ──────────────────────────────────────────────────

interface CenterMapOnDetectorProps {
    latitude: number;
    longitude: number;
    zoomLevel: number;
}

const CenterMapOnDetector2: React.FC<CenterMapOnDetectorProps> = ({ latitude, longitude, zoomLevel }) => {
    const map = useMap();

    useEffect(() => {
        if (latitude && longitude) {
            const startZoom = map.getZoom();
            const startLatLng = map.getCenter();
            const startTime = performance.now();
            const duration = 500;

            const animate = (timestamp: number) => {
                const elapsed = timestamp - startTime;
                const t = Math.min(elapsed / duration, 1);
                const newLat = startLatLng.lat + (latitude - startLatLng.lat) * t;
                const newLng = startLatLng.lng + (longitude - startLatLng.lng) * t;
                const newZoom = startZoom + (zoomLevel - startZoom) * t;
                map.setView([newLat, newLng], newZoom, { animate: false });
                if (t < 1) requestAnimationFrame(animate);
            };

            requestAnimationFrame(animate);
        }
    }, [latitude, longitude, zoomLevel, map]);

    return null;
};

interface Result {
    lon: string;
    lat: string;
    display_name: string;
    boundingbox: string[];
}

const DjangoNominatimProvider = {
    search: (() => {
        let timeout: NodeJS.Timeout | null = null;
        let controller: AbortController | null = null;

        return ({ query }: { query: string }) => {
            if (!query) return Promise.resolve([]);
            if (controller) controller.abort();
            if (timeout) clearTimeout(timeout);

            return new Promise((resolve, reject) => {
                timeout = setTimeout(() => {
                    controller = new AbortController();
                    fetch(`/api/geocode?q=${encodeURIComponent(query)}`, { signal: controller.signal })
                        .then(r => r.json())
                        .then((results: Result[]) => {
                            resolve(results.map(r => ({
                                x: parseFloat(r.lon), y: parseFloat(r.lat),
                                label: r.display_name,
                                bounds: [
                                    [parseFloat(r.boundingbox[0]), parseFloat(r.boundingbox[2])],
                                    [parseFloat(r.boundingbox[1]), parseFloat(r.boundingbox[3])],
                                ],
                                raw: r,
                            })));
                        })
                        .catch(e => { if ((e as any).name !== "AbortError") reject(e); });
                }, 200);
            });
        };
    })(),
};

const SearchControl: React.FC = () => {
    const map = useMap();

    useEffect(() => {
        const searchControl = new (GeoSearchControl as any)({
            provider: DjangoNominatimProvider,
            style: "bar",
            showMarker: false,
            showPopup: true,
            popupFormat: ({ query, result }: { query: string; result: { label: string } }) =>
                `You searched for: ${query}<br>Result: ${result.label}`,
            searchLabel: "Search Address or Zip",
        });
        map.addControl(searchControl);
        return () => { map.removeControl(searchControl); };
    }, [map]);

    return null;
};

// ─── Main component ───────────────────────────────────────────────────────────

const ClientDashboardMapNew = () => {

    // Refresh in case group changes
    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === "selectedGroup") window.location.reload();
        };
        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);


    // ── Auth / group / popup (unchanged) ──
    const { isAuthenticated, getDashboardPermissions, organization_id, organization_permissions, plan } = useAuth();
    const hasFieldTechPermission = organization_permissions?.includes("Field Tech");
    const isStarterPlan = plan === 'Starter';

    const [clientPermissionGroups, setClientPermissionGroups] = useState<{ [key: string]: any }>({});
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [groups, setGroups] = useState<string[]>([]);
    const [chosenGroupCallback, setChosenGroupCallback] = useState<((groupName: string) => void) | null>(null);
    const [savedGroup, setSavedGroup] = useState<string | null>(localStorage.getItem("selectedGroup"));
    const popupRefs = useRef<Record<string, L.Popup>>({});

    // ── Upgrade popup ──
    const [showUpgradePopup, setShowUpgradePopup] = useState(false);

    useEffect(() => {
        const fetchClientPermissions = async () => {
            const permissions2 = await getDashboardPermissions();
            setClientPermissionGroups(permissions2 || {});
            return new Promise((resolve) => {
                setChosenGroupCallback(() => (selectedGroup: string) => {
                    setModalOpen(false);
                    localStorage.setItem("selectedGroup", selectedGroup);
                    resolve(selectedGroup);
                });
            });
        };
        if (isAuthenticated) fetchClientPermissions();
    }, [isAuthenticated, getDashboardPermissions]);

    useEffect(() => {
        if (!savedGroup && clientPermissionGroups && Object.keys(clientPermissionGroups).length > 1) {
            const groupNames = Object.keys(clientPermissionGroups).filter(g => clientPermissionGroups[g]);
            setGroups(groupNames);
            setModalOpen(true);
        }
        if (clientPermissionGroups && Object.keys(clientPermissionGroups).length === 1) {
            const firstGroup = Object.entries(clientPermissionGroups)[0];
            if (firstGroup[1] === true) handleFunction(firstGroup[0]);
            else console.warn("This user does not have permission to access this table");
        }
    }, [clientPermissionGroups, savedGroup]);

    useEffect(() => {
        const storedGroup = localStorage.getItem("selectedGroup");
        if (groups.length === 0) return;
        if (storedGroup && !groups.includes(storedGroup)) {
            localStorage.setItem("selectedGroup", groups[0]);
            return;
        }
        setSavedGroup(storedGroup);
    }, [groups]);

    const handleFunction = (selectedGroup: string) => {
        if (chosenGroupCallback) {
            chosenGroupCallback(selectedGroup);
            localStorage.setItem("selectedGroup", selectedGroup);
            setSavedGroup(selectedGroup);
        }
    };

    // ── Lightning mode ──
    const [lightningMode, setLightningMode] = useState<LightningMode>('past7days');
    const [lightningLayerType, setLightningLayerType] = useState<LightningLayer>('heatmap');

    // Archive data from the new API
    const [archiveLightningData, setArchiveLightningData] = useState<GisArchiveLightning[]>([]);
    const [loadingArchive, setLoadingArchive] = useState(false);
    const [heatmapMax, setHeatmapMax] = useState<number | null>(null);

    // Derive state name from selected group for API filter
    const selectedStateName = useMemo(() => groupToStateName(savedGroup), [savedGroup]);

    const fetchArchiveLightning = useCallback(async (mode: LightningMode) => {
        const range = getDateRangeForMode(mode);
        if (!range) {
            setArchiveLightningData([]);
            return;
        }

        setLoadingArchive(true);
        try {
            const allResults: GisArchiveLightning[] = [];
            let page = 1;
            let hasMore = true;

            while (hasMore) {
                // Build URL — include state filter when we have a resolved state name
                const stateParam = selectedStateName
                    ? `&state=${encodeURIComponent(selectedStateName)}`
                    : '';
                const url = `/apidet/gis_lightning_archive/filter/?start_date=${range.start}&end_date=${range.end}${stateParam}&page=${page}&page_size=100`;
                const res = await fetch(url, { credentials: 'same-origin' });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();

                if (data.results) {
                    allResults.push(...data.results);
                    hasMore = !!data.next;
                } else {
                    allResults.push(...(Array.isArray(data) ? data : []));
                    hasMore = false;
                }
                page++;
            }

            setArchiveLightningData(allResults);
        } catch (err) {
            console.error('Failed fetching archive lightning:', err);
            setArchiveLightningData([]);
        } finally {
            setLoadingArchive(false);
        }
    }, [selectedStateName]);

    // Fetch on mount + whenever mode or selected state changes
    useEffect(() => {
        fetchArchiveLightning(lightningMode);
        const id = setInterval(() => fetchArchiveLightning(lightningMode), 600_000);
        return () => clearInterval(id);
    }, [lightningMode, fetchArchiveLightning]);

    // Derive heatmap points from archive data
    const archiveHeatPoints = useMemo<HeatPoint[]>(() => {
        const merged: Record<string, number> = {};
        for (const item of archiveLightningData) {
            for (const [key, count] of Object.entries(item.strikes)) {
                merged[key] = (merged[key] ?? 0) + count;
            }
        }
        return parseStrikesToHeatPoints(merged);
    }, [archiveLightningData]);

    // Derive county totals from archive data
    const countyTotals = useMemo<Record<string, number>>(() => {
        const totals: Record<string, number> = {};
        for (const item of archiveLightningData) {
            const abbrev = stateToAbbrev[item.region] ?? item.region;
            for (const [county, count] of Object.entries(item.counties || {})) {
                const key = `${county} (${abbrev})`;
                totals[key] = (totals[key] ?? 0) + count;
            }
        }
        return totals;
    }, [archiveLightningData]);

    const countyMaxCount = useMemo(
        () => Math.max(0, ...Object.values(countyTotals)),
        [countyTotals]
    );

    const handleHeatmapExtrema = useCallback((_min: number, max: number) => {
        setHeatmapMax(max);
    }, []);

    const showLightning = lightningMode !== 'none';

    // ── Handle lightning mode change with Starter plan gate ──
    const handleLightningModeChange = (newMode: LightningMode) => {
        if (isStarterPlan && (newMode === 'yesterday' || newMode === 'past3days')) {
            setShowUpgradePopup(true);
            return;
        }
        setLightningMode(newMode);
    };

    // ── Handle layer type change with Starter plan gate ──
    const handleLayerTypeChange = (newLayer: LightningLayer) => {
        if (isStarterPlan && newLayer === 'county') {
            setShowUpgradePopup(true);
            return;
        }
        setLightningLayerType(newLayer);
    };

    // ── GeoJSON overlay ──
    const [hoveredFeature, setHoveredFeature] = useState<{ name: string; density: number } | null>(null);

    // ── Loading states ──
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        const timer = setTimeout(() => setLoading(false), 7000);
        return () => clearTimeout(timer);
    }, []);

    // ── Detector data (unchanged) ──
    const [macsFromAPI, setMacsFromAPI] = useState<Detector[]>([]);
    const [detectorJson, setDetectorJson] = useState<Detector[]>([]);
    const [isMapReady, setIsMapReady] = useState(false);
    const [macsFromTailscale, setMacsFromTailscale] = useState<DetectorObject[]>([]);

    const fetchMACsFromAPI = async (selectedGroup: string | null) => {
        try {
            if (!selectedGroup) return;
            const response = await fetch(`/apidet/${selectedGroup}/`, {
                method: 'GET',
                headers: { "Content-Type": "application/json" },
                credentials: "same-origin",
            });
            if (!response.ok) throw new Error('Network response was not ok');
            const data: Detector[] = await response.json();
            setMacsFromAPI(data);
        } catch (err) {
            console.error(err instanceof Error ? err.message : 'An unknown error occurred');
        }
    };

    useEffect(() => {
        if (savedGroup) {
            fetchMACsFromAPI(savedGroup);
            const interval = setInterval(() => fetchMACsFromAPI(savedGroup), 5000);
            return () => clearInterval(interval);
        }
    }, [savedGroup]);

    useEffect(() => {
        setDetectorJson(macsFromAPI);
        const validDetector = Object.values(macsFromAPI).find(
            d => d.status?.gps_lat && d.status?.gps_long
        );
        if (validDetector) setIsMapReady(true);
    }, [macsFromAPI]);

    const fetchMACsFromTailscale = async () => {
        try {
            const response = await fetch(`/portal/api/relay_fnn_network_basic/`, {
                method: 'GET',
                headers: { "Content-Type": "application/json" },
                credentials: "same-origin",
            });
            if (!response.ok) throw new Error('Network response was not ok');
            const data: DetectorObject[] = await response.json();
            setMacsFromTailscale(data);
        } catch (err) {
            console.error(err instanceof Error ? err.message : 'Error fetching tailscale data');
        }
    };

    useEffect(() => { fetchMACsFromTailscale(); }, []);

    const getDetectorName = (mac: string, tailscaleDevices: DetectorObject[]): string => {
        const match = tailscaleDevices.find(d => normalizeMac(d.receiver) === normalizeMac(mac));
        if (!match) return mac;
        return match.name
            .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .trim();
    };

    const validDetector = Object.values(detectorJson).find(d => {
        const lat = Number(d.status?.gps_lat);
        const long = Number(d.status?.gps_long);
        return lat !== 0 && long !== 0;
    });

    const center: [number, number] = validDetector?.status?.gps_lat && validDetector?.status?.gps_long
        ? [Number(validDetector.status.gps_lat), Number(validDetector.status.gps_long)]
        : [37.7749, -122.4194];

    // ── UI state (unchanged) ──
    const [isFormVisible, setFormVisible] = useState(true);
    const [isArrowVisible, setArrowVisible] = useState(false);
    const [isUserToggled, setUserToggled] = useState(false);
    const formWrapperRef = useRef(null);

    const handleToggleForm = () => {
        setFormVisible(prev => !prev);
        setUserToggled(prev => !prev);
    };

    useEffect(() => {
        const handleResize = () => {
            if (!isUserToggled) {
                setFormVisible(window.innerWidth >= 765);
            }
            setArrowVisible(true);
            document.documentElement.style.setProperty('--arrow-bottom-position', isFormVisible ? '97%' : '-2%');
            document.documentElement.style.setProperty('--z-index', isFormVisible ? '2' : '0');
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [isUserToggled, isFormVisible]);

    const markerRefs = useRef<{ [key: string]: any }>({});
    const [centerCoordinates, setCenterCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);

    const openPopup = (detectorId: string) => {
        const detector = Object.values(detectorJson).find(d => d.mac === detectorId);
        if (detector && markerRefs.current[detectorId]) {
            const latitude = Number(detector.status.gps_lat);
            const longitude = Number(detector.status.gps_long);
            markerRefs.current[detectorId].openPopup();
            setCenterCoordinates(null);
            setTimeout(() => setCenterCoordinates({ latitude, longitude }), 0);
        }
    };

    // ── Filters ──

    // Replace the two separate showOnline/showOffline booleans with one combined state
    type DetectorFilter = 'both' | 'online' | 'offline' | 'none';
    const [detectorFilter, setDetectorFilter] = useState<DetectorFilter>('both');

    const showOnline = detectorFilter === 'both' || detectorFilter === 'online';
    const showOffline = detectorFilter === 'both' || detectorFilter === 'offline';

    const onlineDetectors = Object.values(detectorJson).filter(d => d.online === true);
    const offlineDetectors = Object.values(detectorJson).filter(d => d.online === false);

    const [searchTerm, setSearchTerm] = useState<string>("");
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value.toLowerCase());
    };

    const filteredOnlineDetectors = onlineDetectors.filter(d =>
        getDetectorName(d.mac, macsFromTailscale).toLowerCase().includes(searchTerm)
    );
    const filteredOfflineDetectors = offlineDetectors.filter(d =>
        getDetectorName(d.mac, macsFromTailscale).toLowerCase().includes(searchTerm)
    );

    // ── Gallery / notes (unchanged) ──
    const [galleryData, setGalleryData] = useState<DetectorPhoto[]>([]);

    const fetchGalleryPhotos = async () => {
        try {
            const response = await fetch('/api/detectordashimgs', {
                method: 'GET',
                headers: { "Content-Type": "application/json" },
                credentials: 'same-origin',
            });
            if (!response.ok) throw new Error('Network response was not ok');
            const data: DetectorPhoto[] = await response.json();
            setGalleryData(data);
        } catch (err) {
            console.error(err instanceof Error ? err.message : 'An unknown error occurred');
        }
    };

    useEffect(() => { fetchGalleryPhotos(); }, []);

    const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
    const [previewURL, setPreviewURL] = useState<string | null>(null);

    const getCsrfToken = (): string | null => {
        const metaTag = document.querySelector('meta[name="csrf-token"]');
        return metaTag ? metaTag.getAttribute('content') : null;
    };

    const handleImageUpload = async () => {
        if (!selectedImageFile || !selectedDetector) return;
        const formData = new FormData();
        formData.append("DETECTOR_ID", selectedDetector.mac);
        formData.append("DETECTOR_SITE_NAME", selectedDetector.status.site_name || selectedDetector.mac);
        formData.append("DETECTOR_IMG", selectedImageFile);
        formData.append("organization", String(organization_id));

        try {
            const csrfToken = cookies.get('csrftoken') || getCsrfToken();
            const existing = galleryData.find(img => img.DETECTOR_ID === selectedDetector.mac && img.organization === organization_id);
            const method = existing ? 'PUT' : 'POST';
            const endpoint = existing ? `/api/detectordashimgs/${existing.IMAGE_ID}/` : `/api/detectordashimgs/`;

            const response = await fetch(endpoint, {
                method,
                headers: { 'X-CSRFToken': csrfToken },
                credentials: 'same-origin',
                body: formData,
            });

            if (!response.ok) throw new Error(`Upload failed: ${response.status}`);

            toast.success('Image uploaded successfully!', {
                autoClose: 4000, hideProgressBar: false, closeOnClick: true,
                pauseOnHover: true, draggable: true,
                style: { backgroundColor: '#2d2f31', color: 'white', fontSize: '16px' },
            });

            setPreviewURL(null);
            setSelectedImageFile(null);
            fetchGalleryPhotos();
        } catch (error) {
            console.error("Error uploading image:", error);
        }
    };

    const [showDetecPopup, setShowDetecPopup] = useState(false);
    const [selectedDetector, setSelectedDetector] = useState<Detector | null>(null);
    const [showNotesPopup, setShowNotesPopup] = useState(false);

    const [listOfNotes, setListOfNotes] = useState<DetectorNotes[]>([]);
    const [noteBeingEdited, setNoteBeingEdited] = useState<DetectorNotes | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [editBody, setEditBody] = useState('');

    const fetchNotes = async () => {
        try {
            const response = await fetch('/api/detectordashnotes/', {
                method: 'GET',
                headers: { "Content-Type": "application/json" },
                credentials: "same-origin",
            });
            if (!response.ok) throw new Error('Network response was not ok');
            const data: DetectorNotes[] = await response.json();
            setListOfNotes(data);
        } catch (err) {
            console.error(err instanceof Error ? err.message : 'An unknown error occurred');
        }
    };

    const createNewRowInDB = async (myNotes: DetectorNotes) => {
        try {
            const csrfToken = cookies.get('csrftoken') || getCsrfToken();
            if (!csrfToken) { console.error('CSRF token not found'); return; }
            const { NOTE_ID, ...noteWithoutID } = myNotes;
            const response = await fetch(`/api/detectordashnotes/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
                credentials: 'same-origin',
                body: JSON.stringify({ ...noteWithoutID, organization: organization_id }),
            });
            if (!response.ok) throw new Error(`Server error: ${response.status}`);
            await response.json();
        } catch (err) {
            console.error(err instanceof Error ? err.message : 'An unknown error occurred');
        }
        fetchNotes();
    };

    const deleteRowFromDB = async (noteID: number) => {
        try {
            const csrfToken = cookies.get('csrftoken') || getCsrfToken();
            if (!csrfToken) { console.error('CSRF token not found'); return; }
            const response = await fetch(`/api/detectordashnotes/${noteID}`, {
                method: "DELETE",
                headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
                credentials: 'same-origin',
            });
            if (!response.ok) throw new Error(`Server error: ${response.status}`);
            if (response.headers.get("content-length") !== "0") await response.json();
        } catch (err) {
            console.error(err instanceof Error ? err.message : 'An unknown error occurred');
        }
        fetchNotes();
    };

    const updateRowInDB = async (noteID: number, myNotes: DetectorNotes) => {
        try {
            const csrfToken = cookies.get('csrftoken') || getCsrfToken();
            if (!csrfToken) { console.error('CSRF token not found'); return; }
            const response = await fetch(`/api/detectordashnotes/${noteID}/`, {
                method: "PUT",
                headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
                credentials: 'same-origin',
                body: JSON.stringify({ ...myNotes, organization: organization_id }),
            });
            if (!response.ok) throw new Error(`Server error: ${response.status}`);
            if (response.headers.get("content-length") !== "0") await response.json();
        } catch (err) {
            console.error(err instanceof Error ? err.message : 'An unknown error occurred');
        }
        fetchNotes();
    };

    useEffect(() => { fetchNotes(); }, []);

    listOfNotes.sort((a, b) => {
        const diff = new Date(b.NOTE_SUBMIT_DATE).getTime() - new Date(a.NOTE_SUBMIT_DATE).getTime();
        return diff === 0 ? b.NOTE_ID! - a.NOTE_ID! : diff;
    });

    const handleCloseBigPopup = () => {
        setShowDetecPopup(false);
        setShowNotesPopup(false);
        if (selectedDetector?.mac) {
            const popup = popupRefs.current[selectedDetector.mac];
            if (popup) popup.remove();
        }
        setSelectedDetector(null);
        setPreviewURL(null);
    };

    useEffect(() => {
        const repositionArrow = () => {
            if (!isFormVisible) return;
            const dash = document.querySelector('.my-dash') as HTMLElement | null;
            const arrow = document.querySelector('.circle-around-arrow') as HTMLElement | null;
            if (dash && arrow) arrow.style.bottom = `${dash.getBoundingClientRect().height + 12}px`;
        };
        repositionArrow();
        window.addEventListener('resize', repositionArrow);
        return () => window.removeEventListener('resize', repositionArrow);
    }, [isFormVisible]);

    const [isCompactFilters, setIsCompactFilters] = useState(window.innerWidth <= 600);
    useEffect(() => {
        const handleResize = () => setIsCompactFilters(window.innerWidth <= 600);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // ─── Render ────────────────────────────────────────────────────────────────

    const [isFilterBarOpen, setIsFilterBarOpen] = useState(false);

    return (
        <div style={{ width: "100%", height: "100%" }}>

            <PortalHeader />
            <ToastContainer />

            {/* ── Upgrade popup ── */}
            {showUpgradePopup && (
                <UpgradePopup onClose={() => setShowUpgradePopup(false)} />
            )}

            {modalOpen && (
                <Modal
                    popupTitle={"Select Region"}
                    setOpenModal={setModalOpen}
                    functionToExecute={handleFunction}
                    groupNames={groups}
                />
            )}

            <div className="map-and-dash">
                {isMapReady ? (
                    <>
                        {/* ── Filter bar ── */}
                        <div className={`map-filter-bar ${!isFilterBarOpen ? 'map-filter-bar--collapsed' : ''}`}>
                            <button
                                className="map-filter-bar-toggle"
                                onClick={() => setIsFilterBarOpen(prev => !prev)}
                                aria-label={isFilterBarOpen ? 'Close filters' : 'Open filters'}
                            >
                                <div className="map-filter-bar-label">
                                    <HiOutlineFilter />
                                    <span>Filters</span>
                                </div>
                                <span className="map-filter-bar-toggle-chevron">
                                    {isFilterBarOpen ? '▲' : '▼'}
                                </span>
                            </button>

                            <div className="map-filter-bar-body">

                                {/* ── Status ── */}
                                <div className="filter-section-title">Status</div>

                                <div className={`filter-pill-lightning-wrapper ${detectorFilter !== 'both' ? 'filter-pill-lightning-wrapper--active' : ''}`}>
                                    {detectorFilter === 'online' ? (
                                        <span className="filter-pill-dot det-online filter-section-dot" />
                                    ) : detectorFilter === 'offline' ? (
                                        <span className="filter-pill-dot det-offline filter-section-dot" />
                                    ) : detectorFilter === 'none' ? (
                                        <span className="filter-pill-dot filter-section-dot" style={{ background: 'var(--ink-300)' }} />
                                    ) : (
                                        <span className="filter-section-dot filter-section-dot--both">
                                            <span className="filter-pill-dot det-online" />
                                            <span className="filter-pill-dot det-offline" />
                                        </span>
                                    )}
                                    <div className="filter-pill-lightning-select-wrapper">
                                        <select
                                            className={`filter-pill-lightning-select ${detectorFilter !== 'both' ? 'is-active' : ''}`}
                                            value={detectorFilter}
                                            onChange={e => setDetectorFilter(e.target.value as DetectorFilter)}
                                            aria-label="Detector status filter"
                                            style={{ color: "#0369a1" }}
                                        >
                                            <option value="both">On / Off</option>
                                            <option value="online">Online ({onlineDetectors.length})</option>
                                            <option
                                                value="offline"
                                                disabled={offlineDetectors.length === 0}
                                            >
                                                Offline ({offlineDetectors.length})
                                            </option>
                                            <option value="none">Hide All</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="map-filter-bar-divider" />

                                {/* ── Region ── */}
                                <div className="filter-section-title">Region</div>

                                <div className="region-selection-dropdown">
                                    <select
                                        value={savedGroup || ''}
                                        onChange={e => {
                                            const selected = e.target.value;
                                            setSavedGroup(selected);
                                            localStorage.setItem("selectedGroup", selected);
                                            if (chosenGroupCallback) chosenGroupCallback(selected);
                                        }}
                                    >
                                        <option value="" disabled>Select Region</option>
                                        {Object.keys(clientPermissionGroups)
                                            .filter(g => clientPermissionGroups[g])
                                            .map(g => (
                                                <option key={g} value={g}>
                                                    {g.replace('detectors_', '').replaceAll('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                                </option>
                                            ))}
                                    </select>
                                </div>

                                <div className="map-filter-bar-divider" />

                                {/* ── Lightning Data ── */}
                                <div className="filter-section-title">Lightning Data</div>

                                <div className="filter-pill-lightning-wrapper">
                                    <BsLightningChargeFill className="filter-pill-lightning-icon" />
                                    <div className="filter-pill-lightning-select-wrapper">
                                        <select
                                            className={`filter-pill-lightning-select ${showLightning ? 'is-active' : ''}`}
                                            value={lightningMode}
                                            onChange={e => handleLightningModeChange(e.target.value as LightningMode)}
                                            aria-label="Lightning data range"
                                        >
                                            <option value="none">No Lightning</option>
                                            <option value="past7days">Past 7 Days</option>
                                            <option value="yesterday" disabled={isStarterPlan}>
                                                {isStarterPlan ? '🔒 ' : ''}Yesterday
                                            </option>
                                            <option value="past3days" disabled={isStarterPlan}>
                                                {isStarterPlan ? '🔒 ' : ''}Past 3 Days
                                            </option>
                                        </select>
                                        {isStarterPlan && (
                                            <span
                                                className="filter-pill-lightning-lock"
                                                title="Upgrade to unlock more date ranges"
                                                onClick={() => setShowUpgradePopup(true)}
                                            >
                                                <FaLock />
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* ── Layer type (only when lightning on) ── */}
                                {showLightning && (
                                    <>
                                        <div className={`filter-pill-lightning-wrapper ${lightningLayerType === 'heatmap' || lightningLayerType === 'county' ? 'filter-pill-lightning-wrapper--active' : ''}`}>
                                            {lightningLayerType === 'heatmap' ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="0.85em" height="0.85em" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="filter-pill-lightning-icon" style={{ color: 'var(--brand-500)' }}>
                                                    <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15" />
                                                    <circle cx="12" cy="12" r="7" fill="currentColor" opacity="0.25" />
                                                    <circle cx="12" cy="12" r="4.5" fill="currentColor" opacity="0.5" />
                                                    <circle cx="12" cy="12" r="2.5" fill="currentColor" opacity="1" />
                                                </svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="0.85em" height="0.85em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="filter-pill-lightning-icon" style={{ color: 'var(--brand-500)' }}>
                                                    <rect x="2" y="2" width="20" height="20" rx="1.5" />
                                                    <line x1="9" y1="2" x2="9" y2="22" />
                                                    <line x1="2" y1="9" x2="9" y2="9" />
                                                    <line x1="9" y1="14" x2="24" y2="14" />
                                                    <line x1="16" y1="14" x2="16" y2="22" />
                                                </svg>
                                            )}
                                            <div className="filter-pill-lightning-select-wrapper">
                                                <select
                                                    className={`filter-pill-lightning-select is-active`}
                                                    value={lightningLayerType}
                                                    onChange={e => handleLayerTypeChange(e.target.value as LightningLayer)}
                                                    aria-label="Lightning layer type"
                                                    disabled={isStarterPlan && lightningLayerType === 'heatmap'}
                                                >
                                                    <option value="heatmap">Heatmap</option>
                                                    <option value="county" disabled={isStarterPlan}>
                                                        {isStarterPlan ? '🔒 ' : ''}Counties
                                                    </option>
                                                </select>
                                                {isStarterPlan && (
                                                    <span
                                                        className="filter-pill-lightning-lock"
                                                        title="Upgrade to unlock county breakdown"
                                                        onClick={() => setShowUpgradePopup(true)}
                                                    >
                                                        <FaLock />
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="map-filter-bar-divider" />

                                {/* ── Reset button ── */}
                                <button
                                    type="button"
                                    className="filter-reset-btn"
                                    onClick={() => {
                                        setDetectorFilter('both');
                                        setLightningMode('none');
                                        setLightningLayerType('heatmap');
                                    }}
                                >
                                    Reset filters
                                </button>

                            </div>
                        </div>

                        {/* ── Map ── */}
                        <div className="my-map">
                            <MapContainer
                                center={center}
                                zoom={7}
                                minZoom={2}
                                style={{ width: "100%", height: "100%" }}
                            >
                                <TileLayer
                                    url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                />

                                <SearchControl />

                                {centerCoordinates && (
                                    <CenterMapOnDetector2
                                        latitude={centerCoordinates.latitude}
                                        longitude={centerCoordinates.longitude}
                                        zoomLevel={7}
                                    />
                                )}

                                {/* Lightning layers */}
                                {showLightning && !loadingArchive && archiveHeatPoints.length > 0 && lightningLayerType === 'heatmap' && (
                                    <HeatMapArchiveLayer
                                        data={archiveHeatPoints}
                                        onExtremaChange={handleHeatmapExtrema}
                                    />
                                )}

                                {showLightning && !loadingArchive && lightningLayerType === 'county' && Object.keys(countyTotals).length > 0 && (
                                    <CountyChoroplethLayer countyTotals={countyTotals} />
                                )}

                                {/* Detector markers */}
                                {Object.values(detectorJson).map((currentDetector) => {
                                    if (
                                        currentDetector?.status &&
                                        Object.keys(currentDetector.status).length > 0 &&
                                        currentDetector.status.gps_lat !== null &&
                                        currentDetector.status.gps_long !== null &&
                                        Math.abs(Number(currentDetector.status.gps_lat)) > 1e-6 &&
                                        Math.abs(Number(currentDetector.status.gps_long)) > 1e-6 &&
                                        ((currentDetector.online && showOnline) || (!currentDetector.online && showOffline))
                                    ) {
                                        return (
                                            <Marker
                                                key={currentDetector.mac}
                                                position={[Number(currentDetector.status.gps_lat), Number(currentDetector.status.gps_long)]}
                                                icon={getColorForMarker(currentDetector.online)}
                                                ref={(el) => (markerRefs.current[currentDetector.mac] = el)}
                                            >
                                                <Popup
                                                    ref={(popup) => {
                                                        if (popup) popupRefs.current[currentDetector.mac] = popup;
                                                    }}
                                                >
                                                    <div
                                                        onClick={() => { setSelectedDetector(currentDetector); setShowDetecPopup(true); }}
                                                        style={{ cursor: 'pointer', width: '100%' }}
                                                    >
                                                        <img
                                                            src={galleryData.find(p => p.DETECTOR_ID === currentDetector.mac)?.DETECTOR_IMG || fnnPopupLogo}
                                                            alt=""
                                                            className="fnn-popup-logo"
                                                        />
                                                        <div className="new-popup-content">
                                                            <p><strong>Detector:</strong>{" "}
                                                                <span style={{ color: '#28aee1' }}>{getDetectorName(currentDetector.mac, macsFromTailscale)}</span>
                                                            </p>
                                                            <p><strong>Status:</strong>{" "}
                                                                <span style={{ color: currentDetector.online ? 'green' : 'red' }}>
                                                                    {currentDetector.online ? 'Online' : 'Offline'}
                                                                </span>
                                                            </p>
                                                            <p><strong>Latitude:</strong> {currentDetector.status.gps_lat}</p>
                                                            <p><strong>Longitude:</strong> {currentDetector.status.gps_long}</p>
                                                            {hasFieldTechPermission && (
                                                                <>
                                                                    <p><strong>GPS Fix:</strong>{" "}
                                                                        <span style={{ color: currentDetector.status.gps_fix ? 'green' : 'red' }}>
                                                                            {currentDetector.status.gps_fix ? "Yes" : "No"}
                                                                        </span>
                                                                    </p>
                                                                    <div
                                                                        className="google-maps-button-div"
                                                                        onClick={e => {
                                                                            e.stopPropagation();
                                                                            window.open(
                                                                                `https://www.google.com/maps/search/?api=1&query=${currentDetector.status.gps_lat},${currentDetector.status.gps_long}`,
                                                                                "_blank", "noopener,noreferrer"
                                                                            );
                                                                        }}
                                                                    >
                                                                        <img src={googleMapsLogo} alt="gmaps" />
                                                                        <p style={{ color: "white" }}>Google Maps</p>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Popup>
                                            </Marker>
                                        );
                                    }
                                    return null;
                                })}
                            </MapContainer>

                            {/* Detector detail popup */}
                            {showDetecPopup && selectedDetector && (
                                <div className="detector-popup-overlay">
                                    <div className="popup-container-wrapper">
                                        <div className="popup-scrollable">
                                            <div className={`detector-popup-content ${showNotesPopup ? 'with-notes' : ''}`}>
                                                <button className="close-detector-popup" onClick={handleCloseBigPopup}>x</button>
                                                <img
                                                    src={previewURL || galleryData.find(p => p.DETECTOR_ID === selectedDetector.mac)?.DETECTOR_IMG || fnnPopupLogo}
                                                    alt="Detector"
                                                    className={`selected-detector-popup ${showNotesPopup ? 'img-with-notes' : ''}`}
                                                />
                                                <div className="new-popup-info2-wrapper">
                                                    <h1><span style={{ color: '#28aee1' }}>{getDetectorName(selectedDetector.mac, macsFromTailscale)}</span></h1>
                                                    <div className="new-popup-info2-and-buttons">
                                                        <div className="new-popup-info2">
                                                            <p><strong>Status:</strong>{" "}
                                                                <span style={{ color: selectedDetector.online ? 'green' : 'red' }}>
                                                                    {selectedDetector.online ? 'Online' : 'Offline'}
                                                                </span>
                                                            </p>
                                                            <p><strong>Location:</strong> {Number(selectedDetector.status.gps_lat).toFixed(2)} | {Number(selectedDetector.status.gps_long).toFixed(2)}</p>
                                                            <div>
                                                                <strong>Image:</strong>
                                                                <input
                                                                    id="detector-image-upload"
                                                                    className="custom-detec-image-input"
                                                                    type="file"
                                                                    name="DETECTOR_IMG"
                                                                    accept="image/*"
                                                                    onChange={e => {
                                                                        const file = e.target.files?.[0];
                                                                        if (file) { setSelectedImageFile(file); setPreviewURL(URL.createObjectURL(file)); }
                                                                    }}
                                                                />
                                                                <div className="custom-detec-image-upload-wrapper">
                                                                    <label htmlFor="detector-image-upload" className="custom-detec-image-upload">Upload Image</label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="buttons-from-popup-info2">
                                                            <button className="custom-detec-image-upload" onClick={handleImageUpload}>Save</button>
                                                            <button className="custom-detec-image-upload" onClick={() => setShowNotesPopup(prev => !prev)}>Notes</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {showNotesPopup && (
                                                <div className="notes-popup-content">
                                                    <div className="notes-popup-content-title">
                                                        <h1>Detector Notes</h1>
                                                        <FaPlus
                                                            className="add-notes-icon"
                                                            onClick={() => {
                                                                setNoteBeingEdited({ NOTE_ID: -1, DETECTOR_ID: selectedDetector.mac, NOTE_TITLE: '', NOTE_SUBMIT_DATE: getTodayDateString(), NOTE_EDIT_DATE: '', NOTE_BODY: '' });
                                                                setEditTitle(''); setEditBody('');
                                                            }}
                                                        />
                                                    </div>

                                                    {noteBeingEdited?.NOTE_ID === -1 && (
                                                        <div className="note-edit-form">
                                                            <div className="inner-note-edit">
                                                                <h2>Title</h2>
                                                                <input type="text" value={editTitle} onChange={e => setEditTitle(e.target.value)} placeholder="Note Title" />
                                                            </div>
                                                            <div>
                                                                <div className="inner-note-edit">
                                                                    <h2>Note</h2>
                                                                    <textarea className="note-textarea" value={editBody} onChange={e => setEditBody(e.target.value)} placeholder="Note Body" />
                                                                </div>
                                                            </div>
                                                            <div className="edit-buttons">
                                                                <button onClick={() => {
                                                                    if (!noteBeingEdited) return;
                                                                    if (!editTitle.trim() || !editBody.trim()) {
                                                                        toast.error('Note Title and Body cannot be empty!', { autoClose: 4000, hideProgressBar: false, closeOnClick: true, pauseOnHover: true, draggable: true, style: { backgroundColor: '#2d2f31', color: 'white', fontSize: '16px' } });
                                                                        return;
                                                                    }
                                                                    const { NOTE_ID, ...noteWithoutId } = { ...noteBeingEdited, NOTE_TITLE: editTitle, NOTE_BODY: editBody, NOTE_EDIT_DATE: null };
                                                                    createNewRowInDB(noteWithoutId as Omit<DetectorNotes, 'NOTE_ID'>);
                                                                    setNoteBeingEdited(null); setEditTitle(''); setEditBody('');
                                                                }}>Save</button>
                                                                <button onClick={() => setNoteBeingEdited(null)}>Cancel</button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="notes-popup-content-scrollable">
                                                        {listOfNotes.filter(note => note.DETECTOR_ID === selectedDetector.mac).map(note => (
                                                            <div key={note.NOTE_ID} style={{ width: '100%' }} className="the-overall-note-scroller">
                                                                {noteBeingEdited?.NOTE_ID === note.NOTE_ID ? (
                                                                    <div className="note-edit-form">
                                                                        <div className="inner-note-edit">
                                                                            <h2>Title</h2>
                                                                            <input type="text" value={editTitle} onChange={e => setEditTitle(e.target.value)} placeholder="Note Title" />
                                                                        </div>
                                                                        <div className="notes-date-wrapper">
                                                                            <p><strong>Created on:</strong> {noteBeingEdited?.NOTE_SUBMIT_DATE ?? ''}</p>
                                                                            {noteBeingEdited?.NOTE_EDIT_DATE && <p><strong>Edited on:</strong> {noteBeingEdited.NOTE_EDIT_DATE}</p>}
                                                                        </div>
                                                                        <div className="inner-note-edit">
                                                                            <h2>Body</h2>
                                                                            <textarea className="note-textarea" value={editBody} onChange={e => setEditBody(e.target.value)} placeholder="Note Body" />
                                                                        </div>
                                                                        <div className="edit-buttons">
                                                                            <button onClick={() => {
                                                                                if (!noteBeingEdited || noteBeingEdited.NOTE_ID === undefined) return;
                                                                                if (!editTitle.trim() || !editBody.trim()) {
                                                                                    toast.error('Note Title and Body cannot be empty!', { autoClose: 4000, hideProgressBar: false, closeOnClick: true, pauseOnHover: true, draggable: true, style: { backgroundColor: '#2d2f31', color: 'white', fontSize: '16px' } });
                                                                                    return;
                                                                                }
                                                                                updateRowInDB(noteBeingEdited.NOTE_ID, { ...noteBeingEdited, NOTE_TITLE: editTitle, NOTE_BODY: editBody, NOTE_EDIT_DATE: getTodayDateString() });
                                                                                setNoteBeingEdited(null); setEditTitle(''); setEditBody('');
                                                                            }}>Save</button>
                                                                            <button className="delete-note-button" onClick={() => {
                                                                                if (!noteBeingEdited || noteBeingEdited.NOTE_ID === undefined) return;
                                                                                deleteRowFromDB(noteBeingEdited.NOTE_ID);
                                                                                setNoteBeingEdited(null);
                                                                            }}>Delete</button>
                                                                            <button onClick={() => setNoteBeingEdited(null)}>Cancel</button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="note-entry" onClick={() => { setNoteBeingEdited(note); setEditTitle(note.NOTE_TITLE); setEditBody(note.NOTE_BODY); }}>
                                                                        <div className="note-title-and-date-map">
                                                                            <h2>{note.NOTE_TITLE}</h2>
                                                                            <div className="note-dates-map">
                                                                                <div className="date-and-icon-map"><TbCalendarPlus /><p>{note.NOTE_SUBMIT_DATE}</p></div>
                                                                                <div className="date-and-icon-map">{note.NOTE_EDIT_DATE && <MdOutlineEditCalendar />}<p>{note.NOTE_EDIT_DATE}</p></div>
                                                                            </div>
                                                                        </div>
                                                                        <p className="note-body-p">{note.NOTE_BODY}</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ── Sidebar ── */}
                        <div className="arrow-and-dash">
                            {/* Replace the circle-around-arrow div with this drawer handle */}
                            <div
                                className={`drawer-handle ${isFormVisible ? 'drawer-handle--open' : ''}`}
                                onClick={handleToggleForm}
                                aria-label={isFormVisible ? 'Close panel' : 'Open panel'}
                            >
                                <div className="drawer-handle-bar" />
                                <div className="drawer-handle-bar" />
                                <div className="drawer-handle-bar" />
                            </div>

                            <div ref={formWrapperRef} className={`my-dash ${isFormVisible ? "visible" : ""}`}>
                                <div className="status-search-tip">
                                    <div className="title-and-region">
                                        <h1>Status</h1>
                                    </div>
                                    {/* <div className="input-box">
                                        <input
                                            type="text"
                                            name="detec-search"
                                            placeholder="Search Detectors"
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                        />
                                    </div> */}
                                    <p><strong>Tip</strong>: Click the detector marker and pop-up for additional insights.</p>


                                    {lightningLayerType === 'heatmap' ? (
                                        <HeatLegend min={1} max={heatmapMax} />
                                    ) : (
                                        <CountyLegend max={countyMaxCount} />
                                    )}

                                </div>

                                <div className="online-offline">
                                    {showOnline && (
                                        <div className={`online-ones ${(offlineDetectors.length === 0 || !showOffline) ? 'online-alone' : ''}`}>
                                            <h2>Online</h2>
                                            <div className="detec-group">
                                                {filteredOnlineDetectors.map(detector => {
                                                    if (
                                                        detector.status.gps_lat !== null &&
                                                        detector.status.gps_long !== null &&
                                                        Math.abs(Number(detector.status.gps_lat)) > 1e-6 &&
                                                        Math.abs(Number(detector.status.gps_long)) > 1e-6
                                                    ) {
                                                        return (
                                                            <div className='det-online-wrapper' key={detector.mac}>
                                                                <span className="filter-pill-dot det-online" />
                                                                <p
                                                                    onClick={() => {
                                                                        openPopup(detector.mac);
                                                                        if (window.innerWidth < 700) { setFormVisible(prev => !prev); setUserToggled(prev => !prev); }
                                                                    }}
                                                                >
                                                                    {getDetectorName(detector.mac, macsFromTailscale)}
                                                                </p>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {showOffline && offlineDetectors.length > 0 && (
                                        <div className={`online-ones ${!showOnline ? 'online-alone' : ''}`}>
                                            <h2>Offline</h2>
                                            <div className="detec-group">
                                                {filteredOfflineDetectors.map(detector => {
                                                    if (
                                                        detector.status.gps_lat !== null &&
                                                        detector.status.gps_long !== null &&
                                                        Math.abs(Number(detector.status.gps_lat)) > 1e-6 &&
                                                        Math.abs(Number(detector.status.gps_long)) > 1e-6
                                                    ) {
                                                        return (
                                                            <div className='det-online-wrapper' key={detector.mac}>
                                                                <span className="filter-pill-dot det-offline" />
                                                                <p
                                                                    key={detector.mac}
                                                                    onClick={() => {
                                                                        openPopup(detector.mac);
                                                                        if (window.innerWidth < 700) { setFormVisible(prev => !prev); setUserToggled(prev => !prev); }
                                                                    }}
                                                                >
                                                                    {getDetectorName(detector.mac, macsFromTailscale)}
                                                                </p>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div>
                        {loading ? (
                            <div><LoadingSpinner /></div>
                        ) : (
                            <div className="empty-message">
                                <p>No detectors available in this group.</p>
                                <div className="region-selection-dropdown">
                                    <select
                                        value={savedGroup || ''}
                                        onChange={e => {
                                            const selected = e.target.value;
                                            setSavedGroup(selected);
                                            localStorage.setItem("selectedGroup", selected);
                                            if (chosenGroupCallback) chosenGroupCallback(selected);
                                            window.location.reload();
                                        }}
                                    >
                                        <option value="" disabled>Select Region</option>
                                        {Object.keys(clientPermissionGroups)
                                            .filter(g => clientPermissionGroups[g])
                                            .map(g => (
                                                <option key={g} value={g}>
                                                    {g.replace('detectors_', '').replaceAll('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientDashboardMapNew;